import { now, parseTime } from "common";
import dayjs, { Dayjs } from "dayjs";
import { BaseSchedule, BaseScheduleRepeat, BaseScheduleType } from "types";
import { getAllWeekdays } from "./Weekday";
import { getSeller } from "@/utils/getSeller";

/**
 * Generate all schedule timeslots for the given date.
 */
export const getTimeslots = (schedule: BaseSchedule, date: Dayjs) => {
    if (!includesDate(schedule, date)) {
        return [];
    }

    const { timeRanges = [] } = schedule;

    if (timeRanges.length === 0) {
        // Since there's no time ranges defined, it means it's an all-day schedule.
        return [{ title: schedule.name, start: date.startOf("day"), end: date.endOf("day"), allDay: true }];
    }

    return timeRanges.map((timeRange) => {
        const startTime = parseTime(timeRange.startTime);
        const endTime = parseTime(timeRange.endTime);

        return {
            title: schedule.name,
            start: date.set("hours", startTime.hours).set("minutes", startTime.minutes),
            end: date.set("hours", endTime.hours).set("minutes", endTime.minutes),
            allDay: false,
        };
    });
};

/**
 * Check if the schedule includes the given date.
 */
export const includesDate = (schedule: BaseSchedule, date: Dayjs) => {
    const { timezoneName } = getSeller();
    // Check schedule has no days defined or the date is in the days array
    const includesWeekDay = schedule.days ? schedule.days.length === 0 || schedule.days.includes(date.day()) : true;

    if (schedule.repeat === BaseScheduleRepeat.CUSTOM) {
        return schedule.dates?.some((specificDate: string) => dayjs(specificDate).isSame(date)) && includesWeekDay;
    }

    if (schedule.end) {
        if (dayjs.isDayjs(schedule.end)) {
            // [] denotes both days are included in the check: https://day.js.org/docs/en/plugin/is-between#docsNav
            return date.tz(timezoneName, true).isBetween(schedule.start, schedule.end, "day", "[]") && includesWeekDay;
        }

        return date.isBetween(schedule.start, schedule.end, "day", "[]") && includesWeekDay;
    }

    // TODO: Add another type for equipment schedules since we store start and end on those as timestamps,
    // unlike experiene schedules, where start and end are strings.
    if (dayjs.isDayjs(schedule.start)) {
        return dayjs(schedule.start).isSameOrBefore(date.tz(timezoneName, true)) && includesWeekDay;
    }

    return dayjs(schedule.start).isSameOrBefore(date) && includesWeekDay;
};

export const createBaseSchedule = (values?: Partial<BaseSchedule>) => {
    return {
        name: "",
        type: BaseScheduleType.AVAILABLE,
        start: now(),
        end: null,
        days: getAllWeekdays(),
        timeRanges: [],
        repeat: BaseScheduleRepeat.WEEKLY,
        ...values,
    };
};
